import { Button, Box, Link, ListBox } from '@flos/react-ui';
import type { OPCRowInformation } from '../../../config/flavourConfig';
import { formatCoverableFieldValue } from '../../../utils/coverablesUtil';
import { useState } from 'react';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import { useHippoText } from '../../../hooks/useHippoText';
import usePageNavigation from '../../../hooks/usePageNavigation';
import { type AdjustCoverageAction } from '../../OpcEntryPage/types';

const AdjustableItem = ({ rowInfo }: { rowInfo: OPCRowInformation }): React.JSX.Element => {
    const [showItemDescriptionModal, setShowItemDescriptionModal] = useState(false);
    const [showCallMeModal, setShowCallMeModal] = useState(false);
    const { goToOPCAddCoverageModal, goToOPCEditAttributeModal, goToOPCEditCoveragePage } =
        usePageNavigation();
    const getHippoText = useHippoText();

    const hippoText = {
        changeableItemButton: getHippoText('DSS-SHOPPING-BASKET-ADJUST-DETAILS-ADJUST'),
        removeItemButton: getHippoText('DSS-COMMON-REMOVE'),
        nonChangableItemButton: getHippoText('DSS-SHOPPING-BASKET-ADJUST-DETAILS-CANNOT-ADJUST'),
        modalTitle: getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-HEADER'),
        modalDescription: getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-SUBHEADER'),
        modalSubDescription: getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-BOOK-CALL-SUBHEADER'),
        tilpasSelvrisikoButton: getHippoText('DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE'),
        extendInsurancePeriod: 'Udvid',
        changeInsurancePeriod: 'Skift',
        extendInsurancePeriodText: getHippoText('DSS-ADJUST-INSURANCEPERIOD-TEXT')
    };

    const formattedFieldValue = formatCoverableFieldValue(rowInfo.fieldValue ?? '');
    const getButtonLabel = (productName: string | undefined): string => {
        switch (productName) {
            case 'top_prc_content':
            case 'top_prc_youth_content':
                return 'DSS-COVERABLE-DETAILS-ADAPT-DEDUCTIBLE';
            case 'top_cho_insurance':
                if (formattedFieldValue.includes('5')) {
                    return 'Udvid';
                }
                return 'Skift';
            default:
                return 'DSS-SHOPPING-BASKET-ADJUST-DETAILS-ADJUST';
        }
    };
    const { name, isAdjustableItem, isRemovableItem, labelType, modalInformation, opcInfo } =
        rowInfo;
    const isCHO = opcInfo?.productName === 'top_cho_insurance';
    const isExtendingInsurancePeriod = formattedFieldValue.includes('5') && isCHO;
    const navigateToOPC = (adjustActionOverride?: AdjustCoverageAction): void => {
        if (!rowInfo.opcInfo) return;
        const {
            changeType,
            coverableId,
            productName,
            attribute,
            attributeValue,
            coverageId,
            adjustAction,
            termId
        } = rowInfo.opcInfo;
        switch (changeType) {
            case 'attribut':
                goToOPCEditAttributeModal(productName, coverableId, attribute, attributeValue);
                break;
            case 'tilpas':
                goToOPCEditCoveragePage(
                    productName,
                    coverableId,
                    coverageId,
                    adjustActionOverride ?? adjustAction,
                    termId
                );
                break;
            case 'tilfoej':
                goToOPCAddCoverageModal(productName, coverableId, coverageId);
                break;
            default:
                throw new Error('Unknown change type');
        }
    };

    return (
        <div>
            <ListBox
                className="u-spacing-stack-m"
                title={
                    labelType === 'modalLink' ? (
                        <Link
                            onClick={() => {
                                setShowItemDescriptionModal(true);
                            }}
                            theme="logo"
                        >
                            <h6 className="u-spacing-stack-none">{name}</h6>
                        </Link>
                    ) : (
                        <h6 className="u-spacing-stack-none">{name}</h6>
                    )
                }
                subtitle={formattedFieldValue}
                action={
                    isAdjustableItem ? (
                        <>
                            <Button
                                theme="primary"
                                onClick={() => {
                                    navigateToOPC();
                                }}
                            >
                                {getHippoText(getButtonLabel(opcInfo?.productName))}
                            </Button>
                            {isRemovableItem && (
                                <Button
                                    theme="secondary"
                                    onClick={() => {
                                        navigateToOPC('Remove');
                                    }}
                                >
                                    {hippoText.removeItemButton}
                                </Button>
                            )}
                        </>
                    ) : (
                        <Link
                            onClick={() => {
                                setShowCallMeModal(true);
                            }}
                            theme="primary"
                            className="strong u-spacing-stack-none u-text-center"
                        >
                            {hippoText.nonChangableItemButton}
                        </Link>
                    )
                }
                addonContent={
                    isExtendingInsurancePeriod ? (
                        <Box theme={'highlight'}>
                            <p className="strong 10">{hippoText.extendInsurancePeriodText}</p>
                        </Box>
                    ) : (
                        <></>
                    )
                }
            />
            {showItemDescriptionModal && modalInformation && (
                <modalInformation.modalType
                    showModal={showItemDescriptionModal}
                    modalProps={modalInformation.modalProps}
                    closeModal={() => {
                        setShowItemDescriptionModal(false);
                    }}
                />
            )}
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_call_request"
                    title={hippoText.modalTitle}
                    description={hippoText.modalDescription}
                    subdescription={hippoText.modalSubDescription}
                    showModal
                    closeModal={() => {
                        setShowCallMeModal(false);
                    }}
                />
            )}
        </div>
    );
};

export default AdjustableItem;
